"use client";

import { Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { ReaderStatusBadge, useFreshReaderStatus } from "./ReaderSummary";
import PaymentNotice from "@/app/_components/Payment/PaymentNotice/PaymentNotice";
import { useStripeTerminalContext } from "@/app/_providers/StripeTerminalProvider";
import { DeviceReaderChoice } from "@/app/_helpers/deviceReader";
import { StripeTerminalReader } from "@/app/@types/stripe";

interface ReaderPickerProps {
  // One line above the list, e.g. why the picker is showing.
  notice?: string | null;
  onPick?: (choice: DeviceReaderChoice) => void;
}

// Which reader this device uses. Rendered inline inside the payment screens
// (and Settings) only when the account has more than one registered reader.
// Picking saves the choice for this device; Process then reaches for it
// without asking again.
export const ReaderPicker = ({ notice, onPick }: ReaderPickerProps) => {
  const {
    readers,
    locations,
    deviceReader,
    setDeviceReader,
    hasMultipleLocations,
  } = useStripeTerminalContext();
  useFreshReaderStatus();

  const locationName = (locationId: string | null) =>
    locations.find((l) => l.id === locationId)?.displayName ?? "Other site";

  // Group by site when there is more than one. The device's current site is
  // the site of its saved reader; readers elsewhere stay listed but greyed,
  // because the browser has to be on that site's network to reach them.
  const groups: { id: string | null; name: string; readers: StripeTerminalReader[] }[] =
    hasMultipleLocations
      ? locations
          .map((location) => ({
            id: location.id,
            name: location.displayName,
            readers: readers.filter((r) => r.location === location.id),
          }))
          .concat([
            {
              id: null,
              name: "Other site",
              readers: readers.filter(
                (r) => !locations.some((l) => l.id === r.location)
              ),
            },
          ])
          .filter((group) => group.readers.length > 0)
      : [{ id: null, name: "", readers }];

  const currentLocation = deviceReader?.locationId ?? null;

  const pick = (reader: StripeTerminalReader) => {
    const choice: DeviceReaderChoice = {
      serial: reader.serial_number,
      label: reader.label,
      locationId: reader.location,
    };
    setDeviceReader(choice);
    onPick?.(choice);
  };

  return (
    <Stack gap="xs">
      {notice && <PaymentNotice title="Card reader" message={notice} />}
      <Text size="sm" fw={500}>
        Choose a reader for this device
      </Text>
      {groups.map((group) => (
        <Stack key={group.id ?? "none"} gap={4}>
          {hasMultipleLocations && (
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {group.name}
            </Text>
          )}
          {group.readers.map((reader) => {
            const otherSite =
              hasMultipleLocations &&
              !!currentLocation &&
              reader.location !== currentLocation;
            const selected = deviceReader?.serial === reader.serial_number;
            return (
              <UnstyledButton
                key={reader.id}
                onClick={() => pick(reader)}
                style={{ opacity: otherSite ? 0.55 : 1 }}
                aria-pressed={selected}
              >
                <Paper
                  withBorder
                  p="sm"
                  radius="md"
                  style={{
                    borderColor: selected
                      ? "var(--mantine-primary-color-filled)"
                      : undefined,
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>
                        {reader.label}
                      </Text>
                      {otherSite && (
                        <Text size="xs" c="dimmed">
                          At {locationName(reader.location)}. This device must
                          be on that site&apos;s network.
                        </Text>
                      )}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <ReaderStatusBadge status={reader.status} />
                    </div>
                  </Group>
                </Paper>
              </UnstyledButton>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
};

export default ReaderPicker;
